import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
    ToolConfigCustomToolKit,
    ToolConfigWebhook,
    ToolConfigWebsiteSearch,
    Trunk
} from '@shared/index';
import { CounterService } from '@shared-angular/services/counter.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { Select, SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { BotService } from 'src/app/shared/services/bot.service';
import { FileUploadService, SelectableProjectDocument } from 'src/app/shared/services/file-upload.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { TrunkService } from 'src/app/shared/services/trunk.service';
import { WebscrapeService } from 'src/app/shared/services/webscrape.service';
import { ToastrService } from '@shared-angular/services/toastr.service';

export type SkillTypeEntry = {
    toolId: string;
    href: string;
    name: string;
    description: string;
    icon: string;
    formType: 'form' | 'form-url' | 'name-url' | 'navigate' | 'form-file';
};

@Component({
    selector: 'app-skill-create-dialog',
    templateUrl: './skill-create-dialog.component.html',
    styleUrl: './skill-create-dialog.component.scss',
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule, InputNumberModule, SelectModule, TextareaModule, TabsModule]
})
export class SkillCreateDialogComponent {
    @Input() public visible: boolean = false;
    @Output() public visibleChange = new EventEmitter<boolean>();

    protected selectedType: SkillTypeEntry | null = null;
    protected formName: string = '';
    protected formDescription: string = '';
    protected formUrl: string = 'https://';
    protected formDepth: number | null = 0;
    protected formFileMode: 'project' | 'upload' = 'project';
    protected formSelectedProjectDoc: SelectableProjectDocument | null = null;
    protected formUploadFile: File | null = null;
    protected selectableDocuments: SelectableProjectDocument[] = [];
    protected selectableDocsLoading: boolean = false;
    protected isCreating: boolean = false;
    protected createAttempted = false;
    protected descriptionTouched = false;
    protected nameTouched = false;
    protected urlTouched = false;
    protected depthTouched = false;

    @ViewChild('nameInput') private nameInput?: ElementRef<HTMLInputElement>;
    @ViewChild('urlInput') private urlInput?: ElementRef<HTMLInputElement>;
    @ViewChild('depthInput', { read: ElementRef }) private depthInput?: ElementRef<HTMLElement>;
    @ViewChild('descriptionInput') private descriptionInput?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('documentProjectSelect') private documentProjectSelect?: Select;
    @ViewChild('documentProjectSource') private documentProjectSource?: ElementRef<HTMLDivElement>;
    @ViewChild('documentUploadInput') private documentUploadInput?: ElementRef<HTMLInputElement>;

    constructor(
        private router: Router,
        public rss: RouteShareService,
        private botService: BotService,
        private trunkService: TrunkService,
        private wss: WebscrapeService,
        private counter: CounterService,
        private fileUploadService: FileUploadService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef
    ) { }

    protected readonly skillTypes: SkillTypeEntry[] = [
        {
            toolId: 'customtoolkit',
            href: 'if-then',
            name: $localize`:@@if-then:Wenn-Dann`,
            description: $localize`:@@skill-desc-ifthen:Führt JavaScript-Code aus oder antwortet mit einem festen Text – gesteuert durch definierte Bedingungen.`,
            icon: 'ri-swap-2-fill',
            formType: 'form'
        },
        {
            toolId: 'filesearch',
            href: 'documents',
            name: $localize`:@@documents:Dokumente`,
            description: $localize`:@@skill-desc-documents:Hochgeladene Dateien (PDF, Word, …) werden aufbereitet und dem LLM als passende Textauszüge übergeben. Auch für Downloads und Bildanzeige nutzbar.`,
            icon: 'ri-article-fill',
            formType: 'form-file'
        },
        {
            toolId: 'websitesearch',
            href: 'online-sources',
            name: $localize`:@@online-sources:Online-Quellen`,
            description: $localize`:@@skill-desc-online-sources:Beim Abruf wird der gesamte Seiteninhalt live geladen und dem LLM als Wissen übergeben.`,
            icon: 'ri-cloud-fill',
            formType: 'form-url'
        },
        {
            toolId: 'datatrunk',
            href: 'databases',
            name: $localize`:@@databases:Datenbanken`,
            description: $localize`:@@skill-desc-databases:Eine einfache Schlüssel-Wert-Liste: Wird ein Schlüssel erkannt, wird der zugehörige Wert dem LLM übergeben.`,
            icon: 'ri-database-2-fill',
            formType: 'form'
        },
        {
            toolId: 'websitescrape',
            href: 'offline-websites',
            name: $localize`:@@offline-websites:Offline-Websites`,
            description: $localize`:@@skill-desc-offline-websites:Website-Inhalte werden einmalig heruntergeladen und gespeichert – dem LLM bei Bedarf als Wissen übergeben, ohne Live-Verbindung.`,
            icon: 'ri-cloud-off-fill',
            formType: 'form-url'
        },
        {
            toolId: 'webhook',
            href: 'webhooks',
            name: $localize`:@@webhooks:Webhooks`,
            description: $localize`:@@skill-desc-webhooks:Konfiguriere externe HTTP-Endpunkte zur manuellen Ausführung im Scripting-Bereich. Kein automatischer LLM-Aufruf.`,
            icon: 'ri-webhook-fill',
            formType: 'name-url'
        },
        {
            toolId: 'mcp',
            href: 'mcp-server',
            name: $localize`:@@mcp-server:MCP-Server`,
            description: $localize`:@@skill-desc-mcp:Binde einen MCP-Server ein und gib dem Bot Zugriff auf externe Tools und Datenquellen.`,
            icon: 'ri-server-fill',
            formType: 'navigate'
        }
    ];

    protected selectType(type: SkillTypeEntry): void {
        this.selectedType = type;
        this.formName = '';
        this.formDescription = '';
        this.formUrl = 'https://';
        this.formDepth = 0;
        this.formFileMode = 'project';
        this.formSelectedProjectDoc = null;
        this.formUploadFile = null;
        this.resetValidation();
        if (type.formType === 'navigate') {
            this.navigateTo(type.href);
        } else if (type.formType === 'form-file') {
            void this.loadSelectableDocuments();
        }
    }

    protected back(): void {
        this.selectedType = null;
        this.formName = '';
        this.formDescription = '';
        this.formUrl = 'https://';
        this.formDepth = 0;
        this.formFileMode = 'project';
        this.formSelectedProjectDoc = null;
        this.formUploadFile = null;
        this.resetValidation();
    }

    protected close(): void {
        this.selectedType = null;
        this.formName = '';
        this.formDescription = '';
        this.formUrl = 'https://';
        this.formDepth = 0;
        this.formFileMode = 'project';
        this.formSelectedProjectDoc = null;
        this.formUploadFile = null;
        this.resetValidation();
        this.visibleChange.emit(false);
    }

    protected get isFormValid(): boolean {
        if (!this.selectedType) return false;
        if (this.selectedType.formType === 'name-url') {
            return this.formName.trim() !== '' && this.isUrlValid;
        }
        if (this.selectedType.formType === 'form-file') {
            if (this.formDescription.trim() === '') return false;
            if (this.formFileMode === 'project') return this.formSelectedProjectDoc !== null;
            return this.formUploadFile !== null;
        }
        if (this.formDescription.trim() === '') return false;
        if (this.selectedType.formType === 'form-url' && !this.isUrlValid) return false;
        if (this.selectedType.toolId === 'websitescrape' && !this.isDepthValid) return false;
        return true;
    }

    protected get descriptionInvalid(): boolean {
        return (this.createAttempted || this.descriptionTouched) && this.formDescription.trim() === '';
    }

    protected get nameInvalid(): boolean {
        return (this.createAttempted || this.nameTouched) && this.formName.trim() === '';
    }

    protected get urlInvalid(): boolean {
        return (this.createAttempted || this.urlTouched) && !this.isUrlValid;
    }

    private get isUrlValid(): boolean {
        if (!/^https?:\/\//i.test(this.formUrl.trim())) return false;
        try {
            const url = new URL(this.formUrl.trim());
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    }

    protected get depthInvalid(): boolean {
        return (this.createAttempted || this.depthTouched) && !this.isDepthValid;
    }

    private get isDepthValid(): boolean {
        return this.formDepth === 0 || this.formDepth === 1;
    }

    // Only after create: blur fires while the select overlay or file picker is open.
    protected get documentSourceInvalid(): boolean {
        if (!this.createAttempted) return false;
        if (this.formFileMode === 'project') return this.formSelectedProjectDoc === null;
        return this.formUploadFile === null;
    }

    private resetValidation(): void {
        this.createAttempted = false;
        this.descriptionTouched = false;
        this.nameTouched = false;
        this.urlTouched = false;
        this.depthTouched = false;
    }

    private focusInvalidDocumentField(): void {
        if (this.formDescription.trim() === '') {
            this.descriptionInput?.nativeElement.focus();
        } else if (this.formFileMode === 'upload') {
            this.documentUploadInput?.nativeElement.focus();
        } else if (this.documentProjectSelect) {
            this.documentProjectSelect.focus();
        } else {
            this.documentProjectSource?.nativeElement.focus();
        }
    }

    private focusInvalidField(): void {
        if (this.selectedType?.formType === 'form-file') {
            this.focusInvalidDocumentField();
        } else if (this.selectedType?.formType === 'name-url' && this.formName.trim() === '') {
            this.nameInput?.nativeElement.focus();
        } else if (this.selectedType?.formType !== 'name-url' && this.formDescription.trim() === '') {
            this.descriptionInput?.nativeElement.focus();
        } else if (!this.isUrlValid) {
            this.urlInput?.nativeElement.focus();
        } else if (!this.isDepthValid) {
            this.depthInput?.nativeElement.querySelector('input')?.focus();
        }
    }

    protected async create(): Promise<void> {
        if (!this.selectedType || this.isCreating) return;
        this.createAttempted = true;
        if (!this.isFormValid) {
            this.focusInvalidField();
            return;
        }

        this.isCreating = true;
        try {
            const botId = this.rss.botConfig._id;
            const description = this.formDescription.trim();
            const href = this.selectedType.href;

            if (this.selectedType.toolId === 'filesearch') {
                const name = (await this.botService.suggestName(botId, description)) || this.deriveId(description);
                const skillId = (await this.botService.suggestSkill(botId, description, name)) || this.deriveId(name);

                if (this.formFileMode === 'project') {
                    const res = await this.fileUploadService.addFileFromProject(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        this.formSelectedProjectDoc!._id, botId
                    );
                    if (!res) return;
                    await this.fileUploadService.changeFileProperties(
                        res._id, true, skillId, name, description, botId, false, false
                    );
                    await this.fileUploadService.getFiles(botId);
                    this.close();
                    void this.router.navigate(['/bot-admin', botId, href], { queryParams: { open: res._id } });
                } else {
                    const projectId = this.rss.botConfig.projectId ?? '-';
                    const uploadRes = await this.fileUploadService.uploadProjectDocument(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        this.formUploadFile!, projectId, botId
                    );
                    if (uploadRes.status === 'error' || !uploadRes.botDoc) {
                        this.toastr.error('CREATED');
                        return;
                    }
                    await this.fileUploadService.changeFileProperties(
                        uploadRes.botDoc._id, true, skillId, name, description, botId, true, false
                    );
                    await this.fileUploadService.getFiles(botId);
                    this.close();
                    void this.router.navigate(['/bot-admin', botId, href], { queryParams: { open: uploadRes.botDoc._id } });
                }
                return;
            }

            if (this.selectedType.toolId === 'webhook') {
                const name = this.formName.trim();
                const skillId = (await this.botService.suggestSkill(botId, '', name)) || this.deriveId(name);
                const existingIds = new Set(this.rss.botConfig.toolConfigs.map(t => t.id));
                const newTool = this.buildToolConfig(this.selectedType, name, skillId);
                this.rss.botConfig.toolConfigs.unshift(newTool);
                await this.rss.saveBotConfigField('toolConfigs');
                const savedTool = this.rss.botConfig.toolConfigs.find(t => !existingIds.has(t.id));
                this.close();
                void this.router.navigate(
                    ['/bot-admin', botId, href],
                    savedTool ? { queryParams: { open: savedTool.id } } : {}
                );
                return;
            }

            if (this.selectedType.toolId === 'datatrunk') {
                const name = (await this.botService.suggestName(botId, description)) || this.deriveId(description);
                const skillId = (await this.botService.suggestSkill(botId, description, name)) || this.deriveId(name);
                const trunk: Omit<Trunk, '_id'> = {
                    botId,
                    skillId,
                    placeholder: name,
                    trunkDescription: description,
                    keyDescription: '',
                    active: true
                };
                const res = await this.trunkService.createTrunk(trunk);
                this.rss.datatrunks = [{ ...res.trunk, items: [] }, ...this.rss.datatrunks];
                this.close();
                void this.router.navigate(['/bot-admin', botId, href], { queryParams: { open: res.trunk._id } });
                return;
            }

            if (this.selectedType.toolId === 'websitescrape') {
                const name = (await this.botService.suggestName(botId, description)) || this.deriveId(description);
                const skillId = (await this.botService.suggestSkill(botId, description, name)) || this.deriveId(name);
                const body = {
                    url: this.formUrl.trim(),
                    depth: this.formDepth,
                    description,
                    active: true,
                    skillId,
                    name
                } as unknown as { url: string; depth: number; active: boolean; skillId: string; name: string };
                const res = (await this.wss.postWebscrapeBody(botId, body)).scrape;
                this.rss.webscrapes = [...this.rss.webscrapes, res];
                this.counter.addOfflineWebsite(botId);
                this.close();
                void this.router.navigate(['/bot-admin', botId, href], { queryParams: { open: res._id } });
                return;
            }

            // toolConfig-based types: websitesearch, customtoolkit
            let name = (await this.botService.suggestName(botId, description)) || this.deriveId(description);
            let skillId = (await this.botService.suggestSkill(botId, description, name)) || this.deriveId(name);
            if (this.selectedType.toolId === 'customtoolkit') {
                const existing = this.rss.botConfig.toolConfigs.filter(t => t.tool === 'customtoolkit') as ToolConfigCustomToolKit[];
                name = this.uniquify(name, new Set(existing.map(t => t.name).filter(Boolean)), 30, '_');
                skillId = this.uniquify(skillId, new Set(existing.map(t => t.skillId).filter(Boolean)), 15, '');
            }
            const existingIds = new Set(this.rss.botConfig.toolConfigs.map(t => t.id));
            const newTool = this.buildToolConfig(this.selectedType, name, skillId);
            this.rss.botConfig.toolConfigs.unshift(newTool);
            await this.rss.saveBotConfigField('toolConfigs');

            const savedTool = this.rss.botConfig.toolConfigs.find(t => !existingIds.has(t.id));
            this.close();
            void this.router.navigate(
                ['/bot-admin', botId, href],
                savedTool ? { queryParams: { open: savedTool.id } } : {}
            );
        } finally {
            this.isCreating = false;
            this.cdr.markForCheck();
        }
    }

    private deriveId(from: string): string {
        return from.replace(/[^a-zA-Z0-9]/g, '').replace(/^\d+/, '').slice(0, 20);
    }

    private uniquify(value: string, taken: Set<string>, maxLen: number, sep: string): string {
        for (let i = 2; taken.has(value) && i < 100; i++) {
            const suffix = sep + i;
            value = value.slice(0, maxLen - suffix.length) + suffix;
        }
        return value;
    }

    private async loadSelectableDocuments(): Promise<void> {
        this.selectableDocsLoading = true;
        try {
            this.selectableDocuments = await this.fileUploadService.getSelectableDocuments(
                this.rss.botConfig._id
            );
        } finally {
            this.selectableDocsLoading = false;
            this.cdr.markForCheck();
        }
    }

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.formUploadFile = input.files?.[0] ?? null;
    }

    private navigateTo(href: string): void {
        this.close();
        void this.router.navigate(['/bot-admin', this.rss.botConfig._id, href]);
    }

    private buildToolConfig(type: SkillTypeEntry, name: string, skillId: string): ToolConfigWebhook | ToolConfigWebsiteSearch | ToolConfigCustomToolKit {
        const base = {
            skillId,
            id: '',
            description: this.formDescription.trim(),
            enabled: true,
            params: [] as []
        };

        if (type.toolId === 'webhook') {
            return {
                ...base,
                tool: 'webhook' as const,
                name,
                url: this.formUrl.trim(),
                authMethod: 'none' as const,
                authData: { password: '', user: '' },
                urlMethod: 'get' as const
            } satisfies ToolConfigWebhook;
        }

        if (type.toolId === 'websitesearch') {
            return {
                ...base,
                tool: 'websitesearch' as const,
                name,
                url: this.formUrl.trim()
            } satisfies ToolConfigWebsiteSearch;
        }

        // customtoolkit (wenn-dann)
        return {
            skillId,
            id: '',
            description: this.formDescription.trim(),
            enabled: true,
            tool: 'customtoolkit' as const,
            name,
            type: 'js' as const,
            content: '',
            params: [] as [],
            toolCallAnswer: [''],
            answerMode: 'sequence' as const,
            detectedErrors: false
        } satisfies ToolConfigCustomToolKit;
    }
}
